import "../../style/components/CharacterHeader.scss";

import { IonCol, IonGrid, IonRow } from "@ionic/react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";

import CharacterPortrait from "./CharacterPortrait";
import SegmentSwitcher from "./SegmentSwitcher";
import { setModalVisibility, setPlayerAttr } from "../actions";
import { allSpecificCharacterStates } from "../constants/gamedetails/characterLists";
import { activePlayerSelector, dataDisplaySettingsSelector, frameDataSelector, gameDetailsSelector, recentCharactersSelector, selectedCharactersSelector } from "../selectors";
import { GameName, PlayerData } from "../types";
import { createSegmentSwitcherObject } from "../utils/segmentSwitcherObject";
import { useCharacterSelect } from "../utils/useCharacterSelect";

type CharacterHeaderProps = {
  charName: PlayerData["name"],
  characterHasStates: boolean,
  opponentName: PlayerData["name"],
  charStats: PlayerData["stats"],
  activeGame: GameName
};

const CharacterHeader = ({ charName, characterHasStates, charStats, activeGame }: CharacterHeaderProps) => {
  const [statCategory, setStatCategory] = useState("The Basics");

  const dispatch = useDispatch();
  const history = useHistory();

  const frameData = useSelector(frameDataSelector);
  const gameDetails = useSelector(gameDetailsSelector);
  const dataDisplaySettings = useSelector(dataDisplaySettingsSelector);
  const recentCharacters = useSelector(recentCharactersSelector)[activeGame];
  
  const selectedCharacters = useSelector(selectedCharactersSelector);
  const activePlayer = useSelector(activePlayerSelector);

  const moveNotation =
		dataDisplaySettings.moveNameType === "official"
		  ? "moveName"
		  : dataDisplaySettings.moveNameType === "common"
		    ? "cmnName"
		    : dataDisplaySettings.inputNotationType;

  const labelObj = {};

  Object.keys(gameDetails.statsPoints).forEach(key => {
    const hasStatPoint = Object.keys(gameDetails.statsPoints[key]).some(statCategory =>
      Object.keys(gameDetails.statsPoints[key][statCategory]).some(statPoint => 
        selectedCharacters.playerOne.stats[statPoint]
      )
    );

    if (hasStatPoint) labelObj[key] = key;
  });

  useEffect(() => {
    setStatCategory("The Basics");
  },[activeGame]);

  const handleMoveSelection = (officialMoveName) => {
    const moveToDispatch =
			frameData[charName].moves.normal[officialMoveName][moveNotation]
			  ? frameData[charName].moves.normal[officialMoveName][moveNotation]
			  : frameData[charName].moves.normal[officialMoveName].moveName;
    dispatch(setPlayerAttr(activePlayer, selectedCharacters[activePlayer].name, {selectedMove: moveToDispatch}));
    history.push(`/movedetail/${activeGame}/${selectedCharacters[activePlayer].name}/${selectedCharacters[activePlayer].vtState}/${officialMoveName}`);
  };

  const { onCharacterSelect } = useCharacterSelect({});

  return (
    <IonGrid id="CharacterHeader">
      <IonRow>
        <div className="characters-container">
          <div className="character-portrait-container">
            <CharacterPortrait charName={charName} game={activeGame} charColor={charStats.color as string} remoteImage={charStats.remoteImage as unknown as Boolean} showName={false}
              onClick={() => dispatch(setModalVisibility({ currentModal: "characterSelect", visible: true })) }
            />
          </div>
          <div className="bio-and-recents">
            <div className="bio">
              {charStats.longName ? charStats.longName : charName}
              <span>{charStats.phrase}</span> 
            </div>
            <div className="recents-and-state">
              <div className="recents">
                {recentCharacters && [...recentCharacters].reverse().filter(mappedCharName => {return (mappedCharName !== charName);}).map(mappedCharName => 
                  <div className="character-portrait-container" key={`recent_char_${mappedCharName}`}>
                    <CharacterPortrait charName={mappedCharName} game={activeGame} charColor={charStats.color as string} remoteImage={charStats.remoteImage as unknown as Boolean} showName={false}
                      onClick={() => onCharacterSelect("playerOne", mappedCharName)}
                    />
                  </div>
                )}
              </div>
              {(characterHasStates || activeGame === "SFV") &&
                <div className="state-changer">
                  {activeGame === "SFV" ?
                    <SegmentSwitcher
                      segmentType={"vtrigger"}
                      valueToTrack={selectedCharacters[activePlayer].vtState}
                      labels={ {normal: "Normal", vtOne: "V-Trigger I" , vtTwo: "V-Trigger II"} }
                      clickFunc={ (eventValue) => dispatch(setPlayerAttr(activePlayer, selectedCharacters[activePlayer].name, {vtState: eventValue})) }
                    />
                    : (Object.keys(allSpecificCharacterStates[activeGame]).length > 0) &&
                      <SegmentSwitcher
                        passedClassNames={!characterHasStates ? "collapsed" : "expanded"}
                        segmentType={"vtrigger"}
                        valueToTrack={selectedCharacters[activePlayer].vtState}
                        labels={createSegmentSwitcherObject(gameDetails.specificCharacterStates[selectedCharacters[activePlayer].name])}
                        clickFunc={ (eventValue) => dispatch(setPlayerAttr(activePlayer, selectedCharacters[activePlayer].name, {vtState: eventValue})) }
                      />
                  }
                </div>
              }
            </div>
          </div>
        </div>
      </IonRow>
      <SegmentSwitcher
        key={"Stat Chooser"}
        segmentType={"stat-chooser"}
        valueToTrack={statCategory}
        labels={ labelObj }
        hashtag={
          selectedCharacters[activePlayer].stats.hashtag ?
            selectedCharacters[activePlayer].stats.hashtag as string
            : "false"
        }
        clickFunc={ (eventValue) => setStatCategory(eventValue) }
      />
      <IonRow className="stat-row">
        {gameDetails.statsPoints[statCategory] ?
          gameDetails.statsPoints[statCategory].map(dataRowObj =>
            Object.keys(dataRowObj).map(statKey => 
              charStats[statKey] && charStats[statKey] !== "~" &&
								<IonCol key={`stat-entry-${statKey}`} className="stat-entry" style={statKey === "bestReversal" || statKey === "fastestNormal" ? { cursor: "pointer" } : {}}
								  onClick={() => {
								    if (statKey === "bestReversal") {
								      handleMoveSelection(charStats[statKey]);
								    } else if (statKey === "fastestNormal") {
								      const fastestNormalName = Object.keys(frameData[charName].moves.normal).find(moveEntry =>
								        frameData[charName].moves.normal[moveEntry].moveType === "normal" &&
												!frameData[charName].moves.normal[moveEntry].airmove &&
												frameData[charName].moves.normal[moveEntry].startup === Number(charStats[statKey][0])
								      );
								      handleMoveSelection(fastestNormalName);
								    }
								  }}
								>
								  <h2>
								    {
								      statKey === "bestReversal" && frameData[charName] && frameData[charName].moves.normal[charStats[statKey]]
								        ? frameData[charName].moves.normal[charStats[statKey]][moveNotation] || frameData[charName].moves.normal[charStats[statKey]].moveName
								        : charStats[statKey]
								    }
								  </h2>
								  <p>{dataRowObj[statKey]}</p>
								</IonCol>
            )
          )
          : <IonCol key={"stat-entry-hashtag"} className="stat-entry">
            <h2>Did you get that sick Twitter tech??</h2>
            <p style={{margin: "2px 0 -2px"}}>The sickest twitter tech is <a target="_blank" rel="noreferrer" href="https://twitter.com/D4RK_ONION" style={{color: "inherit"}}>following me</a>... #shameless</p>
          </IonCol>
        }
      </IonRow>
    </IonGrid>
  );
};

export default CharacterHeader;